import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { MutationError, SubmitButton, SuccessPanel } from '../../../components/form-feedback';
import { RadioPillGroup } from '../../../components/radio-pill-group';
import { useDebounce } from '../../../hooks/use-debounce';
import { api } from '../../../lib/api';
import { alertsQuery, herdBaseKey, herdQuery, summaryQuery } from '../../../lib/queries';
import {
  type AddEventFormData,
  AddEventFormSchema,
  type AddEventResponse,
  EVENT_TYPE_LABELS,
  type EventType,
} from '../../../lib/schemas';

export const Route = createFileRoute('/_auth/log/event')({
  beforeLoad: ({ context }) => {
    // biome-ignore lint/suspicious/noExplicitAny: TanStack Router context is typed at route level
    const ctx = context as any;
    if (ctx.session?.role === 'viewer') throw redirect({ to: '/' });
  },
  component: LogEventPage,
});

const EVENT_TYPES = Object.keys(EVENT_TYPE_LABELS) as EventType[];

function LogEventPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [animalIdInput, setAnimalIdInput] = useState('');
  const [successData, setSuccessData] = useState<AddEventResponse | null>(null);
  const debouncedAnimalId = useDebounce(animalIdInput, 300);

  const now = new Date();

  const form = useForm<AddEventFormData>({
    resolver: zodResolver(AddEventFormSchema),
    defaultValues: {
      animal_id: '',
      event_date: now.toISOString().slice(0, 10),
      event_time: now.toTimeString().slice(0, 5),
      notes: '',
    },
  });

  const eventType = form.watch('event_type');

  const { data: animalData, isLoading: isLookingUp } = useQuery({
    ...herdQuery({ search: debouncedAnimalId, limit: 1 }),
    enabled: debouncedAnimalId.length >= 1,
  });

  const foundAnimal = animalData?.animals[0] ?? null;

  const mutation = useMutation({
    mutationFn: (data: AddEventFormData) => api.post<AddEventResponse>('/api/events', data),
    onSuccess: async (data) => {
      setSuccessData(data);
      await queryClient.invalidateQueries({ queryKey: summaryQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: alertsQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: [...herdBaseKey] });
    },
  });

  const onSubmit = form.handleSubmit((data) => {
    mutation.mutate({ ...data, animal_id: animalIdInput });
  });

  // Success state
  if (successData) {
    const updatesCount = Object.keys(successData.herd_register_updates).length;
    return (
      <SuccessPanel
        title="Event Logged"
        onLogAnother={() => {
          setSuccessData(null);
          setAnimalIdInput('');
          form.reset();
        }}
        onHome={() => navigate({ to: '/' })}
      >
        <p className="text-ndd-300">ID: {successData.event_id}</p>
        {updatesCount > 0 && (
          <p className="mt-2 text-sm text-ndd-300">
            Updated {updatesCount} field{updatesCount !== 1 ? 's' : ''} in herd register
          </p>
        )}
      </SuccessPanel>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-4">
      <h1 className="mb-4 text-2xl font-bold">Log Event</h1>

      <form onSubmit={onSubmit} className="space-y-4">
        {/* Animal ID */}
        <div>
          <label htmlFor="animal_id" className="mb-1 block text-sm text-ndd-300">
            Animal Tag *
          </label>
          <input
            id="animal_id"
            type="text"
            inputMode="numeric"
            placeholder="1676"
            value={animalIdInput}
            onChange={(e) => {
              setAnimalIdInput(e.target.value);
              form.setValue('animal_id', e.target.value);
            }}
            className="w-full rounded-lg bg-ndd-900 px-4 py-3 text-lg text-white"
          />
          {isLookingUp && debouncedAnimalId && (
            <p className="mt-1 text-sm text-ndd-400">Looking up...</p>
          )}
          {!isLookingUp && debouncedAnimalId && foundAnimal && (
            <div className="mt-1 rounded-lg bg-ndd-800 px-3 py-2 text-sm">
              <span className="font-semibold">{foundAnimal.name ?? foundAnimal.animal_id}</span>
              <span className="mx-2 text-ndd-400">·</span>
              <span className="text-ndd-300">{foundAnimal.status}</span>
              {foundAnimal.barn_group && (
                <>
                  <span className="mx-2 text-ndd-400">·</span>
                  <span className="text-ndd-300">Group {foundAnimal.barn_group}</span>
                </>
              )}
            </div>
          )}
          {!isLookingUp && debouncedAnimalId && animalData && !foundAnimal && (
            <p className="mt-1 text-sm text-red-400">Animal not found</p>
          )}
          {form.formState.errors.animal_id && (
            <p className="mt-1 text-sm text-red-400">{form.formState.errors.animal_id.message}</p>
          )}
        </div>

        {/* Event Type */}
        <div>
          <p className="mb-2 text-sm text-ndd-300">Event Type *</p>
          <RadioPillGroup
            name="event_type"
            options={EVENT_TYPES.map((type) => ({ value: type, label: EVENT_TYPE_LABELS[type] }))}
            columns={2}
            register={form.register}
            watch={form.watch}
          />
          {form.formState.errors.event_type && (
            <p className="mt-1 text-sm text-red-400">{form.formState.errors.event_type.message}</p>
          )}
        </div>

        {/* Date/Time */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="event_date" className="mb-1 block text-sm text-ndd-300">
              Date
            </label>
            <input
              id="event_date"
              type="date"
              {...form.register('event_date')}
              className="w-full rounded-lg bg-ndd-900 px-3 py-3 text-white"
            />
          </div>
          <div>
            <label htmlFor="event_time" className="mb-1 block text-sm text-ndd-300">
              Time
            </label>
            <input
              id="event_time"
              type="time"
              {...form.register('event_time')}
              className="w-full rounded-lg bg-ndd-900 px-3 py-3 text-white"
            />
          </div>
        </div>

        {/* Dynamic sub-forms */}
        {eventType === 'calving' && (
          <div className="space-y-3 rounded-lg bg-ndd-900/50 p-4">
            <div>
              <p className="mb-2 text-sm text-ndd-300">Calf Sex</p>
              <RadioPillGroup
                name="calf_sex"
                options={[
                  { value: 'heifer', label: 'Heifer' },
                  { value: 'bull', label: 'Bull' },
                  { value: 'stillbirth', label: 'Stillbirth' },
                ]}
                columns={3}
                register={form.register}
                watch={form.watch}
              />
            </div>
            <div>
              <label htmlFor="calf_id" className="mb-1 block text-sm text-ndd-300">
                Calf Tag
              </label>
              <input
                id="calf_id"
                type="text"
                inputMode="numeric"
                placeholder="2001"
                {...form.register('calf_id')}
                className="w-full rounded-lg bg-ndd-900 px-4 py-3 text-white"
              />
            </div>
            <div>
              <p className="mb-2 text-sm text-ndd-300">Ease of Birth</p>
              <RadioPillGroup
                name="ease_of_birth"
                options={[
                  { value: 'unassisted', label: 'Unassisted' },
                  { value: 'easy_pull', label: 'Easy Pull' },
                  { value: 'hard_pull', label: 'Hard Pull' },
                  { value: 'surgical', label: 'Surgical' },
                ]}
                columns={2}
                register={form.register}
                watch={form.watch}
              />
            </div>
          </div>
        )}

        {eventType === 'breeding' && (
          <div>
            <label htmlFor="sire" className="mb-1 block text-sm text-ndd-300">
              Sire Name
            </label>
            <input
              id="sire"
              type="text"
              placeholder="Mogul"
              {...form.register('sire')}
              className="w-full rounded-lg bg-ndd-900 px-4 py-3 text-white"
            />
          </div>
        )}

        {eventType === 'preg_check' && (
          <div>
            <p className="mb-2 text-sm text-ndd-300">Result</p>
            <RadioPillGroup
              name="result"
              options={[
                { value: 'confirmed_pregnant', label: 'Confirmed Pregnant' },
                { value: 'open', label: 'Open' },
              ]}
              columns={2}
              register={form.register}
              watch={form.watch}
            />
          </div>
        )}

        {eventType === 'group_change' && (
          <div>
            <label htmlFor="to_group" className="mb-1 block text-sm text-ndd-300">
              Move to Group
            </label>
            <input
              id="to_group"
              type="text"
              placeholder="2"
              {...form.register('to_group')}
              className="w-full rounded-lg bg-ndd-900 px-4 py-3 text-white"
            />
          </div>
        )}

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="mb-1 block text-sm text-ndd-300">
            Notes
          </label>
          <input
            id="notes"
            type="text"
            {...form.register('notes')}
            className="w-full rounded-lg bg-ndd-900 px-4 py-3 text-white"
          />
        </div>

        {/* Error */}
        {mutation.isError && (
          <MutationError error={mutation.error} fallbackMessage="Failed to log event" />
        )}

        {/* Submit */}
        <SubmitButton isPending={mutation.isPending} />
      </form>
    </div>
  );
}
